import React from 'react';
import { Button } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';

/**
 * Shared "Back to Platform Selector" button. Encodes the convention we settled
 * on across the SPA so every platform doesn't reinvent it (the original audit
 * caught 7 slightly-different versions).
 *
 * For the standard two-level top-left navigation (exit-to-selector PLUS
 * back-to-platform-home), use {@link PlatformNavBar} (./PlatformNavBar.js),
 * which composes two of these buttons — that's what every platform renders.
 *
 * Two visual modes:
 *
 *   onGradient=false (default) — rendered on a plain background (e.g. SitApp,
 *     CiscoApp, Five9App, AgentStudioApp). Uses `variant="light"` with the
 *     platform's accent color so it reads as the same family as the rest of
 *     the page.
 *
 *   onGradient=true — rendered on a hero/gradient background (e.g. CcUtilsHub,
 *     SaToolsHub). Uses `variant="subtle"` + white text so it stays legible
 *     against the colored hero.
 *
 * Props:
 *   - onClick (required): handler from the parent (typically the
 *     `onBackToPlatformSelector` prop the App.js passes in).
 *   - color: Mantine color name for the non-gradient mode (e.g. "violet").
 *     Required when onGradient=false.
 *   - onGradient: boolean. Default false.
 *   - label: override the button text. Defaults to "Back to Platform Selector".
 *   - style / mb: passthrough for layout tweaks (mb defaults to "md").
 */
export default function PlatformBackButton({
  onClick,
  color,
  onGradient = false,
  label = 'Back to Platform Selector',
  style,
  mb = 'md'
}) {
  return (
    <Button
      leftSection={<IconArrowLeft size={16} />}
      variant={onGradient ? 'subtle' : 'light'}
      color={onGradient ? 'white' : color}
      onClick={onClick}
      mb={mb}
      style={onGradient ? { color: 'rgba(255,255,255,0.85)', ...style } : style}
    >
      {label}
    </Button>
  );
}
